/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Custom styles for MDBox
import MDBoxRoot from "components/MDBox/MDBoxRoot";

const MDBox = forwardRef(
  ({ variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow, ...rest }, ref) => {
    // Error handling state
    const [hasError, setHasError] = useState(false);

    // If an error occurred during rendering, fallback to a basic div
    if (hasError) {
      return <div {...rest} ref={ref} style={{ opacity, borderRadius }} />;
    }

    // Try to render the styled component, with error boundary
    try {
      return (
        <MDBoxRoot
          {...rest}
          ref={ref}
          ownerState={{ variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow }}
        />
      );
    } catch (error) {
      console.error("Error rendering MDBox:", error);
      setHasError(true);
      // Fallback rendering
      return <div {...rest} ref={ref} style={{ opacity, borderRadius }} />;
    }
  }
);

// Setting default values for the props of MDBox
MDBox.defaultProps = {
  variant: "contained",
  bgColor: "transparent",
  color: "dark",
  opacity: 1,
  borderRadius: "none",
  shadow: "none",
  coloredShadow: "none",
};

// Typechecking props for the MDBox
MDBox.propTypes = {
  variant: PropTypes.oneOf(["contained", "gradient"]),
  bgColor: PropTypes.string,
  color: PropTypes.string,
  opacity: PropTypes.number,
  borderRadius: PropTypes.string,
  shadow: PropTypes.string,
  coloredShadow: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "none",
    "gold",
  ]),
};

export default MDBox;
